import { PRODUCT_CAROUSEL_WIDTH } from '@Constants/LeftDrawer';
import useProductsPresenter from '@Screens/Products/productSectionPresenter';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React, { useRef } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import type { SwiperControlsProps } from 'react-native-web-swiper';
import Swiper from 'react-native-web-swiper';
import styled from 'styled-components/native';

import OwnableTheme from '../../../assets/OwnableTheme';
import Icon from '../../Common/Icons/Icon';
import StyledProductCard from '../StyledProductCard';

import { useProductsStore } from '~stores/products';

const StyledButton = styled.TouchableOpacity`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  height: 40px;
  width: 40px;
  padding: 0;
  background: white;
  opacity: 0.75;
`;

const StyledProductCarouselCard = styled(StyledProductCard)`
  ${Platform.OS === 'web' &&
  `
    aspect-ratio: 1;
    margin-bottom: 68px;
    margin-top: 74px;
    border-radius: 15px;
  `}
  ${Platform.OS !== 'web' &&
  `
    height: ${PRODUCT_CAROUSEL_WIDTH - 2 * 16}px;
    padding: 0;
    padding-bottom: 4px;
    margin-bottom: 0;
    border-top-right-radius: 0;
    border-top-left-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  `}
  padding: 0;
  overflow: hidden;
`;

const StyledImageContainer = styled.View`
  display: flex;
  flex: 1;
`;

const StyledSwiperImage = styled.Image`
  width: 100%;
  height: 100%;
`;

const StyledDotComponent = styled.Pressable<{ isActive: boolean }>`
  width: 7px;
  height: 7px;
  background-color: ${({ isActive }) =>
    isActive ? OwnableTheme.colors.white : 'rgba(170, 170, 170, 0.8)'};
  border-radius: 10px;
  margin: 0 3px;
`;

const ProductCarouselCard: FC = () => {
  const { currentProduct, currentProductSelectedOption } = useProductsStore();
  const { currentProductImagesForOption } = useProductsPresenter();
  const swiperRef = useRef<Swiper>(null);
  const { width } = useWindowDimensions();

  const isUltraSmall = width <= 375;
  // For small screens move half-way up,
  // and for all shift 20px up
  const marginBottom = isUltraSmall
    ? Math.max((68 - 16) / 2 - 40, 20)
    : 68 - 40;

  if (!currentProduct) {
    return null;
  }

  const swiperPages =
    currentProductImagesForOption?.map((url) => (
      <StyledImageContainer key={url}>
        <StyledSwiperImage
          key={url}
          resizeMode='contain'
          source={{ uri: url }}
        />
      </StyledImageContainer>
    )) ?? [];

  // turn off all controls when there's only one image
  const swiperControlsEnabled = swiperPages.length > 1;

  // noinspection JSUnusedGlobalSymbols
  const swiperControlsConfig: SwiperControlsProps = {
    cellsStyle:
      Platform.OS === 'web'
        ? {
            'bottom-left': {
              borderRadius: 15,
            },
            'bottom-right': {
              borderRadius: 15,
            },
            'top-left': {
              borderRadius: 15,
            },
            'top-right': {
              borderRadius: 15,
            },
          }
        : undefined,
    DotComponent: ({ isActive, onPress }) => (
      <StyledDotComponent onPress={onPress} isActive={isActive} />
    ),
    prevPos: 'left',
    nextPos: 'right',
    dotsTouchable: true,
    dotProps: {
      containerStyle: {
        opacity: swiperControlsEnabled ? 1 : 0,
      },
    },

    // @ts-expect-error - Complains if it's not a class component, but works fine even with functional components
    NextComponent: () => (
      <StyledButton onPress={() => swiperRef.current?.goToNext()}>
        <Icon
          icon='ChevronRight'
          cssSize={28}
          cssColor={OwnableTheme.colors.black}
        />
      </StyledButton>
    ),

    // @ts-expect-error - Complains if it's not a class component, but works fine even with functional components
    PrevComponent: () => (
      <StyledButton onPress={() => swiperRef.current?.goToPrev()}>
        <Icon
          icon='ChevronLeft'
          cssSize={28}
          cssColor={OwnableTheme.colors.black}
        />
      </StyledButton>
    ),
  };

  // the swiper needs to get remounted if the product or option changes
  // (see 'Dynamic Content' @ https://github.com/reactrondev/react-native-web-swiper)
  const swiperKey = `${currentProduct.id}-${
    currentProductSelectedOption?.optionName ?? 'default'
  }-${currentProductSelectedOption?.valueName ?? 'default'}`;

  return (
    <StyledProductCarouselCard style={{ marginBottom }}>
      {/* @ts-expect-error */}
      <Swiper
        key={swiperKey}
        gesturesEnabled={() => swiperControlsEnabled}
        controlsEnabled={swiperControlsEnabled}
        controlsProps={swiperControlsConfig}
        ref={swiperRef}
      >
        {swiperPages}
      </Swiper>
    </StyledProductCarouselCard>
  );
};

export default observer(ProductCarouselCard);
